import { apiClient } from '@/shared/api';

import type {
  BrowseProductsParams,
  BulkProductsStockUpdateRequest,
  BulkUpdateResult,
  ImportResult,
  ProductDetail,
  ProductListRequest,
  ProductListResponse,
  ProductPriceUpdateRequest,
  ProductPublication,
  ProductSearchRequest,
  ProductSearchResponse,
  ProductStateUpdateRequest,
  ProductStockUpdateRequest,
  ProductSuggestRequest,
  ProductSummaryPage,
  ProductUpsertRequest,
  SuggestResponse,
} from '../model/types';

const BASE = '/api/v1/catalog/products';

export const listProducts = async (body: ProductListRequest = {}): Promise<ProductListResponse> =>
  (await apiClient.post<ProductListResponse>(BASE, body)).data;

/**
 * Browse the FULL master catalog (drafts included) — epic-catalog-002, story-024.
 * POST so the filter (incl. exact UUID `ids`) rides in the body and is searched in OpenSearch.
 */
export const browseProducts = async (
  filter: BrowseProductsParams = {},
): Promise<ProductSummaryPage> =>
  (await apiClient.post<ProductSummaryPage>(`${BASE}/browse`, filter)).data;

export const deleteProduct = async (productId: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${productId}`);
};

export const searchProducts = async (body: ProductSearchRequest): Promise<ProductSearchResponse> =>
  (await apiClient.post<ProductSearchResponse>(`${BASE}/search`, body)).data;

export const suggestProducts = async (body: ProductSuggestRequest): Promise<SuggestResponse> =>
  (await apiClient.post<SuggestResponse>(`${BASE}/suggest`, body)).data;

export const getProduct = async (productId: string): Promise<ProductDetail> =>
  (await apiClient.get<ProductDetail>(`${BASE}/${productId}`)).data;

export const upsertProduct = async (
  productId: string,
  body: ProductUpsertRequest,
): Promise<ImportResult> => (await apiClient.put<ImportResult>(`${BASE}/${productId}`, body)).data;

export const updateProductPrice = async (
  productId: string,
  body: ProductPriceUpdateRequest,
): Promise<ImportResult> =>
  (await apiClient.put<ImportResult>(`${BASE}/${productId}/price`, body)).data;

export const updateProductStock = async (
  productId: string,
  body: ProductStockUpdateRequest,
): Promise<ImportResult> =>
  (await apiClient.put<ImportResult>(`${BASE}/${productId}/stock`, body)).data;

export const updateProductState = async (
  productId: string,
  body: ProductStateUpdateRequest,
): Promise<ProductDetail> =>
  (await apiClient.put<ProductDetail>(`${BASE}/${productId}/state`, body)).data;

export const bulkUpdateProductsStock = async (
  body: BulkProductsStockUpdateRequest,
): Promise<BulkUpdateResult> =>
  (await apiClient.put<BulkUpdateResult>(`${BASE}/stock/bulk-update`, body)).data;

// ── Publication (publish / unpublish / archive / state) ──────────────────────
export const publishProduct = async (
  productId: string,
  channelCode?: string,
): Promise<ProductPublication> =>
  (
    await apiClient.post<ProductPublication>(
      `${BASE}/${productId}/publish`,
      undefined,
      channelCode ? { params: { channelCode } } : undefined,
    )
  ).data;

export const unpublishProduct = async (
  productId: string,
  channelCode?: string,
): Promise<ProductPublication> =>
  (
    await apiClient.post<ProductPublication>(
      `${BASE}/${productId}/unpublish`,
      undefined,
      channelCode ? { params: { channelCode } } : undefined,
    )
  ).data;

export const archiveProduct = async (productId: string): Promise<ProductPublication> =>
  (await apiClient.post<ProductPublication>(`${BASE}/${productId}/archive`)).data;

export const restoreProduct = async (productId: string): Promise<ProductPublication> =>
  (await apiClient.post<ProductPublication>(`${BASE}/${productId}/restore`)).data;

export const getProductPublication = async (productId: string): Promise<ProductPublication> =>
  (await apiClient.get<ProductPublication>(`${BASE}/${productId}/publication`)).data;
